import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { TenantConstants } from "../../../constants/index";

import {
  Alert as MuiAlert,
  AlertTitle,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  TextField as MuiTextField,
  Grid,
  CardHeader,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { spacing } from "@mui/system";

import SaveCancel from "../../../components/SaveCancel";
import useTenant from "../../../hooks/useTenant";
import TabPanel from "../../../components/TabPanel";
import { a11yProps } from "../../../utils/common";
import TenantDetail from "./components/TenantDetail";
import LoginDetail from "./components/LoginDetail";
import TenantAddressDetail from "../../../components/TenantAddressDetail";
import ContactDetail from "./components/ContactDetail";
import IpTvSettings from "./components/IpTvSettings";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";
import SmsSettings from "./components/SmsSetting";
import AllowedPlan from "./components/AllowedPlan";
import InvoiceSettings from "./general_settings/components/InvoiceSettings";
import AssignOttPlans from "../../radius/plan_group/components/AssignOttPlans";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const Alert = styled(MuiAlert)(spacing);

const passwordRules = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

function useFocusError(errors, setFocus) {
  useEffect(() => {
    const firstError = Object.keys(errors)[0];
    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);
}

// min 8 characters, 1 upper case letter, 1 lower case letter, 1 numeric digit.

// .matches(
//   /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
//   "Password must contain at least 8 characters, one uppercase, one number and one special case character"
// ),

const validationSchema = Yup.object().shape({
  tenantName: Yup.string().required("Required"),
  mobileNo: Yup.string()
    .required("Required")
    .matches(/^\d{10}$/, "Must be a valid phone number with up to 10 digits"),
  officialMobileNo: Yup.string()
    .required("Required")
    .matches(/^\d{10}$/, "Must be a valid phone number with up to 10 digits"),
  emailId: Yup.string("Enter email id")
    .required("Email id is required")
    .email("Enter a valid email"),
  officialEmailId: Yup.string("Enter email id")
    .required("Email id is required")
    .email("Enter a valid email"),
  userName: Yup.string().required("Required"),

  password: Yup.string().required("Required"),
  confirmPassword: Yup.string()
    .required("Password is required")
    .when("password", {
      is: (val) => (val && val.length > 0 ? true : false),
      then: () => Yup.string().oneOf([Yup.ref("password")], "Both password need to be the same"),
    }),
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  doorNo: Yup.string().required("Required"),
  streetName: Yup.string().required("Required"),
  pinCode: Yup.string().required("Required"),
  villageName: Yup.string().required("Required"),
  townName: Yup.string().required("Required"),
  talukId: Yup.string().required("Required"),
  districtId: Yup.string().required("Required"),
  stateId: Yup.string().required("Required"),
  countryId: Yup.string().required("Required"),
  businessType: Yup.string().required("Required"),
  status: Yup.string().required("Required"),
  sharingType: Yup.string().required("Required"),
  mso: Yup.object()
    .nullable()
    .when("businessType", {
      is: (val) => (val && (val == "LOCAL_CABLE_OPERATOR" || val == "DISTRIBUTOR") ? true : false),
      then: () =>
        Yup.object()
          .shape({
            label: Yup.string().required(),
            value: Yup.string().required(),
          })
          .nullable()
          .required("MSO is required"),
    }),
  // distributor: Yup.object()
  //   .nullable()
  //   .when("businessType", {
  //     is: (val) => (val && val == "LOCAL_CABLE_OPERATOR" ? true : false),
  //     then: () =>
  //       Yup.object()
  //         .shape({
  //           label: Yup.string().required(),
  //           value: Yup.string().required(),
  //         })
  //         .nullable()
  //         .required("Distributor is required"),
  //   }),
  // planGroup: Yup.object()
  //   .nullable()
  //   .when("businessType", {
  //     is: (val) => (val && val == "LOCAL_CABLE_OPERATOR" ? true : false),
  //     then: () =>
  //       Yup.object()
  //         .shape({
  //           label: Yup.string().required(),
  //           value: Yup.string().required(),
  //         })
  //         .nullable()
  //         .required("Sharing group is required"),
  //   }),
  keyId: Yup.string()
    .required("Razor pay key id is required")
    .when("paymentGateway", {
      is: (val) => (val && val.label == "RazorPay" ? true : false),
      then: () => Yup.string().required("Razor pay key id is required"),
      otherwise: () => Yup.string().notRequired(),
    }),
  keyValue: Yup.string()
    .required("Razor pay key value is required")
    .when("paymentGateway", {
      is: (val) => (val && val.label == "RazorPay" ? true : false),
      then: () => Yup.string().required("Razor pay key value is required"),
      otherwise: () => Yup.string().notRequired(),
    }),
  webHookSecret: Yup.string()
    .required("Razor pay webhook is required")
    .when("paymentGateway", {
      is: (val) => (val && val.label == "RazorPay" ? true : false),
      then: () => Yup.string().required("Razor pay webhook is required"),
      otherwise: () => Yup.string().notRequired(),
    }),
  smsPassword: Yup.string()
    .required("Password is required")
    .when("smsProvider", {
      is: (val) => (val && val != null ? true : false),
      then: () => Yup.string().required("Password is required"),
      otherwise: () => Yup.string().notRequired(),
    }),
  smsUserName: Yup.string()
    .required("Username is required")
    .when("smsProvider", {
      is: (val) => (val && val != null ? true : false),
      then: () => Yup.string().required("Username is required"),
      otherwise: () => Yup.string().notRequired(),
    }),
  msoSharing: Yup.number().when("businessType", {
    is: "LOCAL_CABLE_OPERATOR",
    then: () =>
      Yup.number()
        .typeError("MSO sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(100, "Maximum value should be 100")
        .required("MSO Sharing is required"),
  }),
  distSharing: Yup.number().when("businessType", {
    is: "LOCAL_CABLE_OPERATOR",
    then: () =>
      Yup.number()
        .typeError("Dist sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(100, "Maximum value should be 100")
        .required("Distributor Sharing is required"),
  }),
  lcoSharing: Yup.number().when("businessType", {
    is: "LOCAL_CABLE_OPERATOR",
    then: () =>
      Yup.number()
        .typeError("LCO sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(100, "Maximum value should be 100")
        .required("LCO Sharing is required"),
  }),
  agrSharing: Yup.number().when("businessType", {
    is: "MULTI_SERVICE_OPERATOR",
    then: () =>
      Yup.number()
        .typeError("AGR sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(8, "Maximum value should be 8")
        .required("AGR Sharing is required"),
  }),
  ottMsoSharing: Yup.number().when(["businessType", "serviceType"], {
    is: (businessType, serviceType) =>
      businessType === "LOCAL_CABLE_OPERATOR" && serviceType === "INTERNET_OTT",
    then: () =>
      Yup.number()
        .typeError("MSO sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(100, "Maximum value should be 100")
        .required("OTT MSO Sharing is required"),
  }),
  ottDistSharing: Yup.number().when(["businessType", "serviceType"], {
    is: (businessType, serviceType) =>
      businessType === "LOCAL_CABLE_OPERATOR" && serviceType === "INTERNET_OTT",
    then: () =>
      Yup.number()
        .typeError("Dist sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(100, "Maximum value should be 100")
        .required("OTT Distributor Sharing is required"),
  }),
  ottLcoSharing: Yup.number().when(["businessType", "serviceType"], {
    is: (businessType, serviceType) =>
      businessType === "LOCAL_CABLE_OPERATOR" && serviceType === "INTERNET_OTT",
    then: () =>
      Yup.number()
        .typeError("LCO sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(100, "Maximum value should be 100")
        .required("OTT LCO Sharing is required"),
  }),
  serviceType: Yup.string().required("Required"),
  invoiceSettings: Yup.array().when("businessType", {
    is: "MULTI_SERVICE_OPERATOR",
    then: (schema) =>
      schema.of(
        Yup.object().shape({
          invoicePrefix: Yup.string().required("Required"),
          lastInvoiceNumber: Yup.string().required("Required"),
          gstNumber: Yup.string().required("Required"),
          doorNo: Yup.string().required("Required"),
          pinCode: Yup.object()
            .shape({
              label: Yup.string().required(),
              value: Yup.string().required(),
            })
            .nullable()
            .required("Pincode is required"),
          area: Yup.string().required("Required"),
          city: Yup.string().required("Required"),
          state: Yup.string().required("Required"),
          country: Yup.string().required("Required"),
        }),
      ),
  }),
});

const useTabStyles = styled({
  root: {
    justifyContent: "center",
    width: "1000px",
  },
  scroller: {
    flexGrow: "0",
  },
});

function AddTenant() {
  const dispatch = useDispatch();
  const initialValues = {
    tenantName: "",
    mobileNo: "",
    emailId: "",
    officialMobileNo: "",
    officialEmailId: "",
    firstName: "",
    lastName: "",
    gstNo: "",
    businessType: "",
    userName: "",
    doorNo: "",
    streetName: "",
    pinCode: "",
    talukId: "",
    townName: "",
    districtId: "",
    stateId: "",
    countryId: "",
    streetName: "",
    villageName: "",
    mso: null,
    distributor: null,
    planGroup: null,
    sharingType: "FIXED",
    paymentGateway: null,
    razorPayKeyId: "",
    razorPayKeyValue: "",
    razorPayWebHookSecret: "",
    msoSharing: 0,
    distSharing: 0,
    lcoSharing: 0,
    agrSharing: 0,
    ottMsoSharing: 0,
    ottDistSharing: 0,
    ottLcoSharing: 0,
    ipTV: null,
    status: "ACTIVE",
    smsProvider: null,
    smsUserName: "",
    smsPassword: "",
    password: "",
    confirmPassword: "",
    serviceType: "INTERNET",
    allowedPlans: [],
    isSharingAllowed: false,
    invoiceSettings: [
      {
        invoicePrefix: "",
        lastInvoiceNumber: "0",
        tenantInvoiceCounterId: null,
        gstNumber: "",
        doorNo: "",
        pinCode: null,
        area: "",
        city: "",
        state: "",
        country: "",
        postOffices: [],
      },
    ],
    ottPlans: [],
    isAgrEnabled: false,
  };

  const classes = useTabStyles();

  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const {
    reset,
    handleSubmit,
    formState: { errors },
    setFocus,
    watch,
    setValue: setFormValue,
  } = methods;

  // Focus first invalid field
  useFocusError(errors, setFocus);

  const businessType = watch("businessType");
  const serviceType = watch("serviceType");

  React.useEffect(() => {
    let errorTabs = [];

    for (const [validationKey, fields] of Object.entries(errors)) {
      console.log(validationKey, "validationKey");
      for (const [key, tablField] of Object.entries(tabFields)) {
        if (tablField.indexOf(validationKey) !== -1) {
          errorTabs.push(Number(key));
        }
      }
    }
    const latestTab = Math.min(...errorTabs);

    if (latestTab !== Infinity) {
      setValue(latestTab);
    }
  }, [errors]);

  const [status, setStatus] = useState({
    sent: false,
  });

  console.log(errors, "error in add tennt");

  const { t } = useTranslation();
  const { addTenant, hasError, errorMessage } = useTenant();
  const [value, setValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  const navigate = useNavigate();

  const [tabs, setTabs] = useState([]);
  const [tabFields, setTabFields] = useState([]);
  const msoDetails = watch("mso");
  const sharingType = watch("sharingType");

  React.useEffect(() => {
    const fields = [];

    const tabValues = [
      {
        label: "Franchise Details",
        content: <TenantDetail fields={validationSchema.fields} isEditable={false} />,
      },
      { label: "Address", content: <TenantAddressDetail fields={validationSchema.fields} /> },
      { label: "Login Details", content: <LoginDetail fields={validationSchema.fields} /> },
      { label: "Contact Details", content: <ContactDetail fields={validationSchema.fields} /> },
    ];
    fields.push([
      "businessType",
      "mso",
      "distributor",
      "tenantName",
      "officialMobileNo",
      "officialEmailId",
      "sharingType",
      "status",
      "serviceType",
      "agrSharing",
    ]);
    fields.push([
      "doorNo",
      "streetName",
      "pinCode",
      "villageName",
      "townName",
      "talukId",
      "districtId",
      "stateId",
      "countryId",
    ]);
    fields.push(["userName", "password", "confirmPassword"]);
    fields.push(["firstName", "lastName", "emailId", "mobileNo"]);

    if (businessType !== "" && businessType !== "MULTI_SERVICE_OPERATOR") {
      tabValues.push({ label: "Allowed Plan", content: <AllowedPlan /> });

      fields.push([
        "msoSharing",
        "distSharing",
        "lcoSharing",
        "ottMsoSharing",
        "ottDistSharing",
        "ottLcoSharing",
      ]);
    }
    console.log(serviceType, "serviceType2222");
    if (serviceType !== "" && serviceType === "INTERNET_OTT") {
      tabValues.push({
        label: "Assign OTT Plan",
        content: <AssignOttPlans assignedToId={msoDetails?.value} />,
      });
      fields.push([]);
    }
    if (serviceType !== "" && serviceType === "INTERNET") {
      setFormValue("ottMsoSharing", 0);
      setFormValue("ottDistSharing", 0);
      setFormValue("ottLcoSharing", 0);
    }

    tabValues.push({ label: "IpTv Settings", content: <IpTvSettings /> });
    fields.push([]);
    tabValues.push({ label: "Sms Settings", content: <SmsSettings /> });
    fields.push(["smsUserName", "smsPassword", "smsProvider"]);

    if (businessType === "MULTI_SERVICE_OPERATOR") {
      tabValues.push({ label: "Invoice & GST Settings", content: <InvoiceSettings /> });
      fields.push(["invoiceSettings"]);
    }
    setTabs(tabValues);
    setTabFields(fields);
  }, [businessType, serviceType, msoDetails]);

  React.useEffect(() => {
    if (sharingType == "POLICY_WISE") {
      setFormValue("msoSharing", 0);
      setFormValue("distSharing", 0);
      setFormValue("lcoSharing", 0);
      setFormValue("agrSharing", 0);
      setFormValue("ottMsoSharing", 0);
      setFormValue("ottDistSharing", 0);
      setFormValue("ottLcoSharing", 0);
    }
  }, [sharingType]);

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Cpanel</Typography>
        <Typography>Tenant</Typography>
        <Typography>Add</Typography>
      </Breadcrumbs>
    );
  };

  const onSubmit = async (data) => {
    try {
      const msoDto = {
        tenantId: data?.mso?.value,
      };
      const distributorDto = {
        tenantId: data?.distributor?.value,
      };
      data["mso"] = msoDto;
      data["distributor"] = distributorDto;
      data["taluk"] = {
        talukId: data?.talukId,
      };
      data["district"] = {
        districtId: data?.districtId,
      };
      data["state"] = {
        stateId: data?.stateId,
      };
      data["country"] = {
        countryId: data?.countryId,
      };
      console.log(data.iptv, "data?.distributor?.value");
      if (msoDto.tenantId == undefined) {
        delete data["mso"];
      }
      if (distributorDto.tenantId == undefined) {
        delete data["distributor"];
      }

      if (
        (data.businessType == "LOCAL_CABLE_OPERATOR" || data.businessType == "BULK_BANDWIDTH") &&
        data.allowedPlans.length == 0
      ) {
        dispatch(showErrorSnackbar("Please selected allowed plans"));
        return;
      }

      if (data.ipTV != null) {
        data["ipTV"] = {
          ipTvId: data.iptv.value,
        };
      }

      if (data.paymentGateway != undefined) {
        data["paymentGateway"] = {
          paymentGatewayId: data?.paymentGateway?.value,
          paymentGatewayName: data?.paymentGateway?.label,
        };
      }
      if (data.smsProvider != null && data.smsProvider != undefined) {
        data["smsProvider"] = {
          smsProviderId: data?.smsProvider?.value,
          smsProviderName: data?.smsProvider?.label,
        };
      }

      data["tenantInvoiceCounters"] = [];
      if (businessType !== "" && businessType === "MULTI_SERVICE_OPERATOR") {
        for (let i = 0; i < data["invoiceSettings"].length; i++) {
          data["tenantInvoiceCounters"].push({
            tenantId: null,
            invoicePrefix: data["invoiceSettings"][i]["invoicePrefix"],
            lastInvoiceNumber: data["invoiceSettings"][i]["lastInvoiceNumber"],
            tenantInvoiceCounterId: data["invoiceSettings"][i]["tenantInvoiceCounterId"],
            gstNumber: data["invoiceSettings"][i]["gstNumber"],
            doorNo: data["invoiceSettings"][i]["doorNo"],
            pinCode: data["invoiceSettings"][i]["pinCode"]["value"],
            area: data["invoiceSettings"][i]["area"],
            city: data["invoiceSettings"][i]["city"],
            state: data["invoiceSettings"][i]["state"],
            country: data["invoiceSettings"][i]["country"],
          });
        }
      }

      //let payload = Object.fromEntries(Object.entries(data).filter(([_, v]) => v != ""));

      //let newPayload = clearEmpties(payload);
      // payload["isSharingAllowed"] = isSharingAllowed;

      // console.log(data, "payloadpayload11111");

      await addTenant(data);
      dispatch(showSuccessSnackbar("Tenant added successfully!"));
      reset();
    } catch (err) {
      console.error(err, "errerrerr");
      //setStatus({ sent: false });
      //setErrorSubmit({ submit: err.message });
      let messageDetails = [];
      if (err.errors != undefined) {
        let errors = err.errors;
        for (let error in errors) {
          messageDetails.push(errors[error].field + " " + errors[error].message);
        }
        dispatch(showErrorSnackbar(messageDetails.join(",")));
      } else {
        dispatch(showErrorSnackbar(err.message || err.detail));
      }
    }
  };

  const clearEmpties = (o) => {
    for (var k in o) {
      if (!o[k] || typeof o[k] !== "object") {
        continue; // If null or not an object, skip to the next iteration
      }

      // The property is an object
      if (Object.keys(o[k]).length === 0) {
        delete o[k]; // The object had no properties, so delete that property
      }
      return o;
    }
  };

  const cancelBtn = () => {
    navigate("/cpanel/tenant/list");
  };

  const hasTabError = (index) =>
    Object.keys(errors).some((field) => tabFields[index]?.includes(field));

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader title={t("add_franchise")} subheader={breadCrump()} />
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  classes={{ scroller: classes.scroller }}
                  value={value}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  aria-label="tenant"
                  sx={{
                    justifyContent: "center",
                  }}
                >
                  {tabs.map((tab, index) => (
                    <Tab
                      key={index}
                      label={<TabLabel text={tab.label} error={hasTabError(index)} />}
                      {...a11yProps(index)}
                    />
                  ))}
                </Tabs>
                {tabs.map((tab, index) => (
                  <TabPanel key={index} value={value} index={index}>
                    {tab.content}
                  </TabPanel>
                ))}
              </Box>

              <SaveCancel cancelBtn={cancelBtn} resetForm={reset} />
            </form>
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

const TabLabel = ({ text, error }) => (
  <Box display="flex" alignItems="center">
    {text}
    {error && (
      <Typography color="error" ml={1}>
        •
      </Typography>
    )}
  </Box>
);

export default AddTenant;
